import { motion } from "framer-motion";
import { MainNav } from "../components/MainNav";
import { useLanguage } from "../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const content = language === 'sv' ? {
    title: "Integritetspolicy",
    seoTitle: "Integritetspolicy | Petrobell",
    seoDescription: "Läs om hur Petrobell AB behandlar dina personuppgifter i enlighet med GDPR och dataskyddsförordningen.",
    back: "Tillbaka",
    lastUpdated: "Senast uppdaterad: 9 januari 2025",
    sections: [
      {
        title: "1. Personuppgiftsansvarig",
        content: `Petrobell AB (org.nr: 556268-1782) är personuppgiftsansvarig för behandlingen av dina personuppgifter. 

Kontaktuppgifter:
Petrobell AB
Ranhammarsvägen 4
168 67 Bromma
E-post: admin@petrobell.se`
      },
      {
        title: "2. Vilka personuppgifter samlar vi in?",
        content: `Vi samlar in följande personuppgifter när du använder vår webbplats eller våra tjänster:

• Namn och företagsnamn
• E-postadress
• Telefonnummer
• Leveransadress
• Beställningsinformation (produkter, volymer, datum)
• Teknisk information (IP-adress, webbläsarinformation) via cookies`
      },
      {
        title: "3. Varför behandlar vi dina personuppgifter?",
        content: `Vi behandlar dina personuppgifter för följande ändamål:

• Hantera och genomföra beställningar av drivmedel
• Kommunicera med dig angående leveranser
• Administrera kundrelationer
• Förbättra vår webbplats och våra tjänster
• Uppfylla rättsliga förpliktelser (bokföring, etc.)`
      },
      {
        title: "4. Rättslig grund för behandling",
        content: `Vi behandlar dina personuppgifter baserat på följande rättsliga grunder:

• Avtal: När du gör en beställning behandlar vi dina uppgifter för att fullgöra avtalet.
• Samtycke: För marknadsföring och icke-nödvändiga cookies.
• Berättigat intresse: För att förbättra våra tjänster och webbplats.
• Rättslig förpliktelse: För bokföring och andra lagstadgade krav.`
      },
      {
        title: "5. Hur länge sparar vi dina uppgifter?",
        content: `Vi sparar dina personuppgifter så länge det är nödvändigt för det syfte de samlades in:

• Kunduppgifter: Under pågående kundrelation och upp till 24 månader därefter.
• Bokföringsunderlag: 7 år enligt bokföringslagen.
• Cookie-data: Enligt cookiens livslängd (se avsnitt om cookies).`
      },
      {
        title: "6. Delning av personuppgifter",
        content: `Vi kan dela dina personuppgifter med:

• Transportörer och leveranspartners för att genomföra leveranser
• IT-leverantörer som tillhandahåller system och hosting
• Myndigheter när vi är skyldiga enligt lag

Vi säljer aldrig dina personuppgifter till tredje part.`
      },
      {
        title: "7. Cookies",
        content: `Vår webbplats använder cookies för att:

• Säkerställa att webbplatsen fungerar korrekt (nödvändiga cookies)
• Analysera hur webbplatsen används (analys-cookies)

Du kan hantera dina cookie-inställningar via cookiebannern som visas vid ditt första besök. Du kan också radera cookies via din webbläsares inställningar.`
      },
      {
        title: "8. Dina rättigheter",
        content: `Enligt GDPR har du följande rättigheter:

• Rätt till tillgång: Du har rätt att få information om vilka uppgifter vi har om dig.
• Rätt till rättelse: Du kan begära att felaktiga uppgifter korrigeras.
• Rätt till radering: Du kan begära att vi raderar dina personuppgifter.
• Rätt till begränsning: Du kan begära att behandlingen begränsas.
• Rätt till dataportabilitet: Du kan få ut dina uppgifter i ett maskinläsbart format.
• Rätt att invända: Du kan invända mot behandling baserad på berättigat intresse.

För att utöva dina rättigheter, kontakta oss på admin@petrobell.se.`
      },
      {
        title: "9. Klagomål",
        content: `Om du anser att vi behandlar dina personuppgifter i strid med GDPR har du rätt att lämna in ett klagomål till Integritetsskyddsmyndigheten (IMY).

Integritetsskyddsmyndigheten
Box 8114
104 20 Stockholm
www.imy.se`
      },
      {
        title: "10. Ändringar i integritetspolicyn",
        content: `Vi kan komma att uppdatera denna integritetspolicy. Vid väsentliga ändringar informerar vi dig via vår webbplats. Den senaste versionen finns alltid tillgänglig på denna sida.`
      }
    ]
  } : {
    title: "Privacy Policy",
    seoTitle: "Privacy Policy | Petrobell",
    seoDescription: "Read about how Petrobell AB processes your personal data in accordance with GDPR.",
    back: "Back",
    lastUpdated: "Last updated: January 9, 2025",
    sections: [
      {
        title: "1. Data Controller",
        content: `Petrobell AB (org.no: 556268-1782) is the data controller for the processing of your personal data.

Contact details:
Petrobell AB
Ranhammarsvägen 4
168 67 Bromma, Sweden
Email: admin@petrobell.se`
      },
      {
        title: "2. What personal data do we collect?",
        content: `We collect the following personal data when you use our website or services:

• Name and company name
• Email address
• Phone number
• Delivery address
• Order information (products, volumes, dates)
• Technical information (IP address, browser information) via cookies`
      },
      {
        title: "3. Why do we process your personal data?",
        content: `We process your personal data for the following purposes:

• Manage and fulfill fuel orders
• Communicate with you regarding deliveries
• Administer customer relationships
• Improve our website and services
• Fulfill legal obligations (accounting, etc.)`
      },
      {
        title: "4. Legal basis for processing",
        content: `We process your personal data based on the following legal grounds:

• Contract: When you place an order, we process your data to fulfill the contract.
• Consent: For marketing and non-essential cookies.
• Legitimate interest: To improve our services and website.
• Legal obligation: For accounting and other statutory requirements.`
      },
      {
        title: "5. How long do we store your data?",
        content: `We store your personal data for as long as necessary for the purpose they were collected:

• Customer data: During the ongoing customer relationship and up to 24 months thereafter.
• Accounting records: 7 years according to the Accounting Act.
• Cookie data: According to the cookie's lifespan (see cookie section).`
      },
      {
        title: "6. Sharing of personal data",
        content: `We may share your personal data with:

• Carriers and delivery partners to carry out deliveries
• IT providers that provide systems and hosting
• Authorities when we are required by law

We never sell your personal data to third parties.`
      },
      {
        title: "7. Cookies",
        content: `Our website uses cookies to:

• Ensure the website functions properly (necessary cookies)
• Analyze how the website is used (analytics cookies)

You can manage your cookie settings via the cookie banner that appears on your first visit. You can also delete cookies via your browser settings.`
      },
      {
        title: "8. Your rights",
        content: `Under GDPR, you have the following rights:

• Right of access: You have the right to information about what data we have about you.
• Right to rectification: You can request that incorrect data be corrected.
• Right to erasure: You can request that we delete your personal data.
• Right to restriction: You can request that processing be restricted.
• Right to data portability: You can receive your data in a machine-readable format.
• Right to object: You can object to processing based on legitimate interest.

To exercise your rights, contact us at admin@petrobell.se.`
      },
      {
        title: "9. Complaints",
        content: `If you believe we are processing your personal data in violation of GDPR, you have the right to file a complaint with the Swedish Authority for Privacy Protection (IMY).

Swedish Authority for Privacy Protection
Box 8114
104 20 Stockholm, Sweden
www.imy.se`
      },
      {
        title: "10. Changes to the privacy policy",
        content: `We may update this privacy policy. In case of significant changes, we will inform you via our website. The latest version is always available on this page.`
      }
    ]
  };

  return (
    <div className="min-h-screen relative">
      {/* Full page background */}
      <div className="fixed inset-0 bg-[url('/images/hav-bg.jpg')] bg-cover bg-center" />
      <div className="fixed inset-0 bg-black/50" />
      
      <SEOHead
        title={content.seoTitle}
        description={content.seoDescription}
        path="/privacy-policy"
        language={language}
      />
      <MainNav />
      
      {/* Hero Section */}
      <div className="relative pt-32 pb-16">
        <div className="container mx-auto px-4">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors group"
          >
            <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            <span>{content.back}</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="mb-6">
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                {content.title}
              </h1>
            </div>
            <p className="text-white/50">{content.lastUpdated}</p>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <main className="relative pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-6">
            {content.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-white/10 hover:border-white/20 transition-colors"
              >
                <h2 className="text-xl font-semibold text-white mb-4">
                  {section.title}
                </h2>
                <div className="text-white/70 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
